import { Head, Link, router, usePage } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { StatusChip } from "@/components/ui/status-chip"
import { Switch } from "@/components/ui/switch"
import type { PageProps } from "@/types/inertia"

type BrandStats = {
  key: "gisela" | "sanctuary"
  name: string
  turns: number
  outcomes: Record<string, number>
  cache_hit_rate: number | null
  estimated_usd: number
  avg_latency_ms: number | null
  tools: Record<string, number>
  per_day: { date: string; turns: number }[]
  unanswered: { summary: string; count: number }[]
}

type Props = PageProps & {
  enabled: boolean
  configured: boolean
  days: number
  knowledge_base_tokens: number
  knowledge_base_budget: number
  brands: BrandStats[]
}

const OUTCOME_LABELS: Record<string, string> = {
  answered: "Answered",
  unanswered: "Not on the site",
  crisis: "Crisis signpost",
  refused: "Refused",
  error: "Error",
}

function percent(value: number | null) {
  return value == null ? "—" : `${Math.round(value * 100)}%`
}

function DayBars({ days }: { days: BrandStats["per_day"] }) {
  const max = Math.max(1, ...days.map((d) => d.turns))
  return (
    <div className="flex h-16 items-end gap-px" role="img" aria-label="Questions per day, last 30 days">
      {days.map((day) => (
        <div
          key={day.date}
          className="flex-1 rounded-t-sm bg-accent"
          style={{ height: `${Math.max(4, (day.turns / max) * 100)}%`, opacity: day.turns === 0 ? 0.15 : 1 }}
          title={`${day.date}: ${day.turns}`}
        />
      ))}
    </div>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-hairline bg-surface px-3 py-2">
      <h6>{label}</h6>
      <p className="tabular-nums">{value}</p>
    </div>
  )
}

export default function AssistantIndex() {
  const { enabled, configured, days, knowledge_base_tokens, knowledge_base_budget, brands } = usePage<Props>().props
  const live = enabled && configured

  return (
    <AdminShell>
      <Head title="Site Assistant">
        <meta name="description" content="Usage, spend and unanswered questions for the public site assistant." />
        <meta property="og:title" content="Site Assistant" />
        <meta property="og:description" content="Usage, spend and unanswered questions for the public site assistant." />
      </Head>

      <PageHeader
        title="Site Assistant"
        description={`The "Ask a question" box on both public sites, answering from the sites' own content. Last ${days} days. Nothing visitors type is stored — only outcomes, tokens and the model's summary of questions the site couldn't answer.`}
        actions={
          <div className="flex items-center gap-3">
            <StatusChip status={live ? "active" : "inactive"} />
            <label htmlFor="site-assistant-switch" className="flex items-center gap-2 whitespace-nowrap font-normal text-ink-body">
              <Switch
                id="site-assistant-switch"
                size="sm"
                checked={enabled}
                onChange={(next) =>
                  router.patch("/admin/features/site_assistant", { enabled: next }, { preserveScroll: true })
                }
              />
              {enabled ? "Switched on" : "Switched off"}
            </label>
            <Link href="/admin/features" className="btn btn-secondary btn-small">
              All switches
            </Link>
          </div>
        }
      />

      {!configured && (
        <div className="callout mb-6">
          <strong>Claude isn't configured.</strong>
          <p className="mt-1">
            Set <code>ANTHROPIC_API_KEY</code> for this environment; until then the widget stays hidden whatever the
            feature switch says.
          </p>
        </div>
      )}

      <div className="mb-6 grid gap-3 sm:grid-cols-2">
        <Stat
          label="Knowledge base size"
          value={`~${knowledge_base_tokens.toLocaleString()} tokens (budget ${knowledge_base_budget.toLocaleString()})`}
        />
        <Stat
          label="Total questions"
          value={brands.reduce((sum, b) => sum + b.turns, 0).toLocaleString()}
        />
      </div>

      <div className="flex flex-col gap-8">
        {brands.map((brand) => (
          <section key={brand.key} className="rounded-xl border border-hairline bg-surface p-4">
            <h2>{brand.name}</h2>
            <div className="mt-3 grid gap-3 sm:grid-cols-4">
              <Stat label="Questions" value={brand.turns.toLocaleString()} />
              <Stat label="Estimated spend" value={`$${brand.estimated_usd.toFixed(2)}`} />
              <Stat label="Prompt cache hits" value={percent(brand.cache_hit_rate)} />
              <Stat
                label="Average reply time"
                value={brand.avg_latency_ms == null ? "—" : `${(brand.avg_latency_ms / 1000).toFixed(1)}s`}
              />
            </div>

            <div className="mt-4">
              <h6>Questions per day</h6>
              <DayBars days={brand.per_day} />
            </div>

            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <h6>Outcomes</h6>
                <ul className="mt-1">
                  {Object.entries(brand.outcomes).map(([outcome, count]) => (
                    <li key={outcome} className="flex justify-between gap-2">
                      <span>{OUTCOME_LABELS[outcome] ?? outcome}</span>
                      <span className="tabular-nums">{count}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h6>Tools used</h6>
                {Object.keys(brand.tools).length === 0 ? (
                  <p className="mt-1">None yet.</p>
                ) : (
                  <ul className="mt-1">
                    {Object.entries(brand.tools).map(([tool, count]) => (
                      <li key={tool} className="flex justify-between gap-2">
                        <span>{tool}</span>
                        <span className="tabular-nums">{count}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="mt-4">
              <h6>Questions the site couldn't answer</h6>
              {brand.unanswered.length === 0 ? (
                <p className="mt-1">None recorded.</p>
              ) : (
                <ul className="mt-1 divide-y divide-hairline">
                  {brand.unanswered.map((item) => (
                    <li key={item.summary} className="flex justify-between gap-3 py-1.5">
                      <span>{item.summary}</span>
                      <span className="tabular-nums">×{item.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        ))}
      </div>
    </AdminShell>
  )
}
